import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Cpu, KeyRound, Sparkles, Volume2, Loader2, Save, PlayCircle, ShieldCheck, DollarSign, RefreshCw, Info, RotateCcw, FileText,
} from "lucide-react";

const CUSTOM_MODEL_SENTINEL = "__custom__";

/** Super-admin page — /admin/ai-config */
const FEATURE_LABELS = {
  question_rationale:     { label: "Question Rationale",          desc: "AI-generated explanation for each question.", kind: "text" },
  ai_learning_monitor:    { label: "AI Learning Monitor",         desc: "Weekly insights on a student's performance.", kind: "text" },
  regenerate_explanation: { label: "Regenerate Explanation",      desc: "Admin action: re-run rationale for a question.", kind: "text" },
  tts_read_aloud:         { label: "Read Aloud (TTS)",            desc: "Student's one-shot Speaker button.",           kind: "tts" },
  tts_podcast:            { label: "Podcast Mode (TTS)",          desc: "Module podcast gapless narration.",            kind: "tts" },
};

const tierBadge = (tier) => {
  const label = "💰".repeat(tier);
  const color =
    tier === 1 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : tier === 2 ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-rose-50 text-rose-700 border-rose-200";
  return <span className={`text-[10px] rounded-md border px-1.5 py-0.5 ${color}`}>{label}</span>;
};

export default function AdminAiConfig() {
  const [data, setData] = useState(null);
  const [busyFeature, setBusyFeature] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [byoInputs, setByoInputs] = useState({});
  // per-feature "Custom model" text input — shown when user picks "Custom model…"
  const [customModelInputs, setCustomModelInputs] = useState({});
  // per-provider live-fetched model list (from POST /admin/ai-config/refresh-models)
  const [providerModels, setProviderModels] = useState({});
  const [refreshingProvider, setRefreshingProvider] = useState(null);

  const load = async () => {
    const r = await api.get("/admin/ai-config");
    setData(r.data);
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-8 text-slate-500 text-sm">Loading AI configuration…</div>;

  const { config, catalog, prompts = [] } = data;

  const modelsForKind = (kind) =>
    kind === "tts" ? catalog.tts_models : catalog.text_models;

  const saveFeature = async (feature, patch) => {
    setBusyFeature(feature);
    try {
      await api.put("/admin/ai-config/feature", { feature, ...patch });
      toast.success(`${FEATURE_LABELS[feature].label} saved`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusyFeature(null);
    }
  };

  const testFeature = async (feature) => {
    setBusyFeature(feature + ":test");
    setTestResults((s) => ({ ...s, [feature]: { loading: true } }));
    try {
      const r = await api.post("/admin/ai-config/test", { feature });
      setTestResults((s) => ({ ...s, [feature]: { ok: true, data: r.data } }));
      toast.success(`Test succeeded for ${FEATURE_LABELS[feature].label}`);
    } catch (e) {
      setTestResults((s) => ({ ...s, [feature]: { ok: false, error: e?.response?.data?.detail || e?.message } }));
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setBusyFeature(null);
    }
  };

  const saveKey = async (provider, patch) => {
    try {
      await api.put("/admin/ai-config/key", { provider, ...patch });
      toast.success(`${provider} key updated`);
      setByoInputs((s) => ({ ...s, [provider]: "" }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const refreshProviderModels = async (provider) => {
    setRefreshingProvider(provider);
    try {
      const r = await api.post("/admin/ai-config/refresh-models", { provider });
      setProviderModels((s) => ({ ...s, [provider]: r.data.models || [] }));
      toast.success(`Fetched ${r.data.count} models from ${provider}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not fetch ${provider} models`);
    } finally {
      setRefreshingProvider(null);
    }
  };

  const savePrompt = async (name, text) => {
    try {
      const r = await api.put("/admin/ai-config/prompt", { name, text });
      const missing = r.data?.missing_placeholders || [];
      if (missing.length) {
        toast.warning(`Saved, but these placeholders are missing: ${missing.join(", ")}`);
      } else {
        toast.success("Prompt saved");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const resetPrompt = async (name) => {
    if (!window.confirm("Reset this prompt to the default text? Your custom edits will be lost.")) return;
    try {
      await api.post("/admin/ai-config/prompt/reset", { name, text: "" });
      toast.success("Prompt reset to default");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-ai-config-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 flex items-center gap-2">
          <Cpu className="w-3.5 h-3.5" /> AI Model Manager
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Configure AI models</h1>
        <p className="text-slate-600 dark:text-slate-300 mt-1 text-sm">
          Choose which model powers each AI feature. Bring your own OpenAI/Anthropic/Google key,
          or use the Emergent Universal Key (default). If a model fails at runtime, the app falls
          back to GPT-4o via Emergent and logs a warning.
        </p>
      </div>

      {/* API keys */}
      <Card className="p-5 border-slate-200 dark:border-slate-700 space-y-4" data-testid="ai-keys-card">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-emerald-600" />
          <h2 className="font-display text-lg font-bold">API keys</h2>
        </div>

        {/* Emergent Universal */}
        <KeyRow
          title="Emergent Universal Key"
          description="Default. One key covers OpenAI + Anthropic + Gemini + Nano Banana + Sora + Whisper."
          enabled={config.keys.emergent?.enabled}
          hasKey={true}
          keyHint=""
          hideInput
          onToggle={(enabled) => saveKey("emergent", { enabled, api_key: "" })}
          testid="key-emergent"
        />
        {/* BYO providers */}
        {["openai", "anthropic", "google"].map((prov) => (
          <KeyRow
            key={prov}
            title={prov[0].toUpperCase() + prov.slice(1) + " (Bring your own key)"}
            description={`Use your own ${prov} API key instead of the Emergent Universal Key for ${prov} models.`}
            enabled={config.keys[prov]?.enabled}
            hasKey={config.keys[prov]?.has_key}
            keyHint={config.keys[prov]?.key_hint}
            inputValue={byoInputs[prov] || ""}
            onInput={(v) => setByoInputs((s) => ({ ...s, [prov]: v }))}
            onSave={(v) => saveKey(prov, { enabled: true, api_key: v })}
            onToggle={(enabled) => saveKey(prov, { enabled, api_key: "" })}
            onRefreshModels={config.keys[prov]?.has_key ? () => refreshProviderModels(prov) : null}
            refreshing={refreshingProvider === prov}
            liveModels={providerModels[prov]}
            testid={`key-${prov}`}
          />
        ))}
      </Card>

      {/* Feature → model mapping */}
      <Card className="p-5 border-slate-200 dark:border-slate-700 space-y-6" data-testid="ai-features-card">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-600" />
          <h2 className="font-display text-lg font-bold">Feature-model mapping</h2>
        </div>

        {catalog.features.map((feature) => {
          const meta = FEATURE_LABELS[feature] || { label: feature, desc: "", kind: "text" };
          const current = config.features[feature] || {};
          const busy = busyFeature === feature || busyFeature === feature + ":test";
          const result = testResults[feature];

          return (
            <div key={feature} className="border-t border-slate-100 dark:border-slate-800 pt-5 first:border-t-0 first:pt-0" data-testid={`feature-${feature}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {meta.kind === "tts" ? <Volume2 className="w-4 h-4 text-fuchsia-600" /> : <Sparkles className="w-4 h-4 text-indigo-600" />}
                    <div className="font-semibold text-sm">{meta.label}</div>
                    <Badge variant="outline" className="text-[10px]">{meta.kind === "tts" ? "TTS" : "Text"}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{meta.desc}</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => testFeature(feature)}
                  disabled={busy}
                  data-testid={`test-${feature}`}
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                  <span className="ml-1.5">Test now</span>
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Model</Label>
                  {(() => {
                    const options = modelsForKind(meta.kind);
                    const isCustom = !options.some((m) => m.model === current.model);
                    return (
                      <>
                        <Select
                          value={isCustom ? CUSTOM_MODEL_SENTINEL : current.model}
                          onValueChange={(v) => {
                            if (v === CUSTOM_MODEL_SENTINEL) {
                              // Show the text input — don't save yet
                              setCustomModelInputs((s) => ({ ...s, [feature]: current.model || "" }));
                              return;
                            }
                            const opt = options.find((m) => m.model === v);
                            if (!opt) return;
                            saveFeature(feature, {
                              provider: opt.provider,
                              model: opt.model,
                              temperature: current.temperature,
                              voice: current.voice,
                            });
                          }}
                        >
                          <SelectTrigger data-testid={`model-${feature}`}>
                            <SelectValue placeholder={isCustom ? `Custom: ${current.model}` : undefined} />
                          </SelectTrigger>
                          <SelectContent>
                            {options.map((m) => (
                              <SelectItem key={m.model} value={m.model}>
                                <span className="flex items-center gap-2">
                                  <span className="text-slate-400 text-[10px] uppercase">{m.provider}</span>
                                  {m.label} {tierBadge(m.tier)}
                                </span>
                              </SelectItem>
                            ))}
                            <SelectItem value={CUSTOM_MODEL_SENTINEL} data-testid={`model-${feature}-custom`}>
                              <span className="flex items-center gap-2 text-indigo-700">
                                <Sparkles className="w-3 h-3" /> Custom model ID…
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        {(isCustom || customModelInputs[feature] !== undefined) && (
                          <div className="mt-2 flex items-center gap-2">
                            <Select
                              value={customModelInputs[feature + ":provider"] || current.provider || "openai"}
                              onValueChange={(v) => setCustomModelInputs((s) => ({ ...s, [feature + ":provider"]: v }))}
                            >
                              <SelectTrigger className="h-9 w-32 text-xs" data-testid={`model-${feature}-custom-provider`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="openai">OpenAI</SelectItem>
                                <SelectItem value="anthropic">Anthropic</SelectItem>
                                <SelectItem value="google">Google</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input
                              placeholder="e.g. gpt-5.7 or claude-opus-5"
                              value={customModelInputs[feature] ?? current.model ?? ""}
                              onChange={(e) => setCustomModelInputs((s) => ({ ...s, [feature]: e.target.value }))}
                              className="h-9 text-xs font-mono"
                              data-testid={`model-${feature}-custom-input`}
                            />
                            <Button
                              size="sm"
                              onClick={() => {
                                const id = (customModelInputs[feature] || "").trim();
                                if (!id) { toast.error("Enter a model id"); return; }
                                const prov = customModelInputs[feature + ":provider"] || current.provider || "openai";
                                saveFeature(feature, {
                                  provider: prov, model: id,
                                  temperature: current.temperature, voice: current.voice,
                                });
                                setCustomModelInputs((s) => {
                                  const next = { ...s }; delete next[feature]; delete next[feature + ":provider"]; return next;
                                });
                              }}
                              data-testid={`model-${feature}-custom-save`}
                            >
                              <Save className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                        <p className="text-[10px] text-slate-500 mt-1.5 flex items-start gap-1">
                          <Info className="w-3 h-3 mt-0.5 shrink-0" />
                          Emergent adds new models regularly. Use <span className="font-semibold">Custom model ID</span> to try one before it's in this list.
                        </p>
                      </>
                    );
                  })()}
                </div>

                {meta.kind === "tts" ? (
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500">Voice</Label>
                    <Select
                      value={current.voice || "nova"}
                      onValueChange={(v) => saveFeature(feature, {
                        provider: current.provider, model: current.model, voice: v,
                      })}
                    >
                      <SelectTrigger data-testid={`voice-${feature}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {catalog.tts_voices.map((v) => (
                          <SelectItem key={v} value={v}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500 flex justify-between">
                      <span>Temperature</span>
                      <span className="tabular-nums text-slate-700 font-semibold">{(current.temperature ?? 0.4).toFixed(2)}</span>
                    </Label>
                    <Slider
                      min={0} max={1.5} step={0.05}
                      value={[current.temperature ?? 0.4]}
                      onValueChange={(v) => setData((d) => ({
                        ...d,
                        config: {
                          ...d.config,
                          features: { ...d.config.features, [feature]: { ...current, temperature: v[0] } },
                        },
                      }))}
                      onValueCommit={(v) => saveFeature(feature, {
                        provider: current.provider, model: current.model, temperature: v[0],
                      })}
                      className="mt-3"
                      data-testid={`temperature-${feature}`}
                    />
                  </div>
                )}
              </div>

              {result && (
                <div className={`mt-3 text-xs rounded-md p-3 ${result.ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-rose-50 text-rose-800 border border-rose-200"}`}>
                  {result.loading ? "Testing…"
                   : result.ok ? (
                    result.data?.kind === "tts"
                      ? <>✅ TTS test OK — {result.data.model} · voice {result.data.voice} · {result.data.bytes} bytes · via {result.data.key_source}</>
                      : <>✅ {result.data.provider}/{result.data.model} · via {result.data.key_source} · reply: <span className="italic">{(result.data.reply || "").slice(0, 160)}</span></>
                  )
                   : <>❌ {result.error}</>}
                </div>
              )}
            </div>
          );
        })}
      </Card>

      {/* ═══════ Editable Prompts ═══════ */}
      {prompts.length > 0 && (
        <Card className="p-6 space-y-4" data-testid="ai-config-prompts">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-bold">Prompts</h2>
            <span className="text-xs text-slate-500">Edit the text sent to the LLM for each feature.</span>
          </div>
          {prompts.map((p) => (
            <PromptEditor
              key={p.name}
              prompt={p}
              onSave={(text) => savePrompt(p.name, text)}
              onReset={() => resetPrompt(p.name)}
            />
          ))}
        </Card>
      )}

      <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5" />
        API keys are stored server-side and never sent back to the browser after save.
      </div>
    </div>
  );
}

function KeyRow({ title, description, enabled, hasKey, keyHint, hideInput, inputValue, onInput, onSave, onToggle, onRefreshModels, refreshing, liveModels, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap border-t border-slate-100 dark:border-slate-800 pt-4 first:border-t-0 first:pt-0" data-testid={testid}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold flex items-center gap-2">
          {title}
          {hasKey && !hideInput && (
            <Badge variant="outline" className="text-[10px]">
              <DollarSign className="w-3 h-3 mr-1" /> stored …{keyHint}
            </Badge>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">{description}</div>
        {!hideInput && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Input
              type="password"
              placeholder={hasKey ? "leave blank to keep current" : "sk-… or your key"}
              value={inputValue}
              onChange={(e) => onInput(e.target.value)}
              className="max-w-md h-9 text-sm font-mono"
              data-testid={`${testid}-input`}
            />
            <Button
              size="sm"
              onClick={() => inputValue.trim() && onSave(inputValue.trim())}
              disabled={!inputValue.trim()}
              data-testid={`${testid}-save`}
            >
              <Save className="w-3.5 h-3.5 mr-1" /> Save
            </Button>
            {onRefreshModels && (
              <Button
                size="sm"
                variant="outline"
                onClick={onRefreshModels}
                disabled={refreshing}
                title="Fetch live model list from provider"
                data-testid={`${testid}-refresh-models`}
              >
                {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                <span className="ml-1.5">Refresh models</span>
              </Button>
            )}
          </div>
        )}
        {Array.isArray(liveModels) && liveModels.length > 0 && (
          <div className="mt-2 text-[11px] text-slate-600 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-2 max-h-32 overflow-auto font-mono" data-testid={`${testid}-live-models`}>
            <div className="text-[10px] uppercase text-slate-400 mb-1">{liveModels.length} models available — copy any id into a feature's Custom model input:</div>
            {liveModels.join(" · ")}
          </div>
        )}
      </div>
      <div className="pt-1">
        <Switch checked={!!enabled} onCheckedChange={onToggle} data-testid={`${testid}-toggle`} />
      </div>
    </div>
  );
}

function PromptEditor({ prompt, onSave, onReset }) {
  const [text, setText] = useState(prompt.text);
  // Re-sync when reset happens or config reloads with new default.
  useEffect(() => { setText(prompt.text); }, [prompt.text]);
  const dirty = text !== prompt.text;
  const placeholders = prompt.placeholders || [];
  return (
    <div className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 space-y-2" data-testid={`prompt-${prompt.name}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-semibold flex items-center gap-2">
            {prompt.label}
            {prompt.is_default
              ? <Badge variant="outline" className="text-[10px]">default</Badge>
              : <Badge className="text-[10px] bg-indigo-100 text-indigo-700 hover:bg-indigo-100">customised</Badge>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">{prompt.description}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm" variant="outline"
            onClick={onReset}
            disabled={prompt.is_default}
            title="Restore the built-in default prompt"
            data-testid={`prompt-${prompt.name}-reset`}
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
          </Button>
          <Button
            size="sm"
            onClick={() => onSave(text)}
            disabled={!dirty || !text.trim()}
            data-testid={`prompt-${prompt.name}-save`}
          >
            <Save className="w-3.5 h-3.5 mr-1" /> Save
          </Button>
        </div>
      </div>

      {placeholders.length > 0 && (
        <div className="flex flex-wrap gap-1 text-[10px]">
          <span className="uppercase text-slate-400 mr-1">Placeholders:</span>
          {placeholders.map((ph) => {
            const ok = text.includes(ph);
            return (
              <button
                key={ph}
                type="button"
                onClick={() => setText(text + (text.endsWith("\n") || text === "" ? "" : " ") + ph)}
                className={`px-1.5 py-0.5 rounded font-mono border ${
                  ok
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-amber-50 text-amber-700 border-amber-300"
                }`}
                title={ok ? "Present in prompt" : "Missing — click to insert"}
              >
                {ph}
              </button>
            );
          })}
        </div>
      )}

      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={14}
        className="font-mono text-xs leading-relaxed"
        placeholder="Prompt text — use the placeholders above to inject values."
        data-testid={`prompt-${prompt.name}-textarea`}
      />
      <div className="text-[10px] text-slate-400 flex items-center justify-between">
        <span>{text.length.toLocaleString()} chars · max 20,000</span>
        {dirty && <span className="text-amber-600 font-semibold">Unsaved changes</span>}
      </div>
    </div>
  );
}
